// @ts-check
'use strict';

/**
 * One call for everything the Word report renders from a visit: header facts,
 * counts, site condition, key risks, tiers, dashboard, register, outlook and
 * the three summary tiers.
 */

const report = require('./report');
const { buildReportSummaries } = require('./summaries');

/** @typedef {import('./scales').Band} Band */
/** @typedef {import('./report').Area} Area */

/**
 * Report header facts and the assessor's stored overrides.
 *
 * @typedef {object} VisitMeta
 * @property {string} [siteName]
 * @property {string} [clientName]
 * @property {string} [location]
 * @property {string} [inspectionDate]          'YYYY-MM-DD'
 * @property {string} [assessorName]
 * @property {string} [clientContact]
 * @property {string} [siteDirector]
 * @property {string | null} [sector]           a SECTORS key
 * @property {string} [buildingAge]
 * @property {string} [notableItems]
 * @property {Band | null} [overallConditionOverride]
 * @property {string[] | null} [keyRisksOverride]
 * @property {string | null} [overallSummaryOverride]
 * @property {Record<string, string>} [sectorSummaryOverride]  by sector key
 * @property {Record<string, string>} [areaSummaryOverride]    by area name
 */

/**
 * @typedef {object} Visit
 * @property {string} scale        a SCALES key — the contract's rating scale
 * @property {Area[]} areas
 * @property {VisitMeta} [meta]
 */

/**
 * @param {Visit} visit
 */
function buildViewModelCore(visit) {
  const { scale, areas = [] } = visit;
  const meta = visit.meta || {};
  const stats = report.computeStats(areas, scale);
  return {
    stats,
    areaStats: report.computeAreaStats(areas, scale),
    overallBand: report.overallBand(stats, meta.overallConditionOverride),
    keyRisks: report.aggregateKeyRisks(areas, meta.keyRisksOverride),
    benefitCount: report.itemsNotNeedingRepaint(stats),
    recommendationTiers: report.buildRecommendationTiers(areas, scale),
    dashboardRows: report.buildDashboardRows(areas, scale),
    register: report.buildRegister(areas, scale),
    outlookRows: report.buildOutlookRows(areas, scale),
  };
}

/** @typedef {ReturnType<typeof buildViewModelCore>} ReportViewModel */

/**
 * @param {Visit} visit
 */
function buildReportViewModel(visit) {
  const meta = visit.meta || {};
  const vm = buildViewModelCore(visit);
  return { meta, scale: visit.scale, ...vm, summaries: buildReportSummaries(meta, vm) };
}

module.exports = { buildReportViewModel };
